import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import KpiCard from '../components/KpiCard'
import AlertsFeed from '../components/AlertsFeed'
import { readingsApi } from '../api/readings'
import { scadaApi } from '../api/scada'
import { useAuthStore } from '../store/authStore'
import { edgeConnStatus, EDGE_STATUS_LABEL, EDGE_STATUS_DOT, EDGE_STATUS_TEXT } from '../utils/edgeStatus'
import { hasModule } from './SettingsPage'
import InfrastructureFocus from './dashboard/InfrastructureFocus'

function useReadingStats() {
  const { data: meters } = useQuery({
    queryKey: ['meters'],
    queryFn: () => readingsApi.getMeters({ limit: 500 }).then((r) => r.data),
    staleTime: 60_000,
  })

  const { data: readings } = useQuery({
    queryKey: ['readings-recent'],
    queryFn: () => readingsApi.list({ limit: 500 }).then((r) => r.data),
    staleTime: 30_000,
  })

  const totalMeters  = meters?.length ?? 0
  const activeMeters = meters?.filter((m) => m.active).length ?? 0

  // Readings this month
  const thisMonth = new Date().toISOString().slice(0, 7)
  const readingsThisMonth = readings?.filter((r) => r.reading_date.slice(0, 7) === thisMonth).length ?? 0

  // Coverage: % of active meters with a reading this month
  const activeIds = new Set(meters?.filter((m) => m.active).map((m) => m.id) ?? [])
  const coveredIds = new Set(
    readings
      ?.filter((r) => r.reading_date.slice(0, 7) === thisMonth && activeIds.has(r.meter_id))
      .map((r) => r.meter_id) ?? []
  )
  const coverage = activeMeters > 0 ? Math.round((coveredIds.size / activeMeters) * 100) : 0

  // Group readings by month for consumption trend
  const byMonth: Record<string, number[]> = {}
  readings?.forEach((r) => {
    const month = r.reading_date.slice(0, 7) // YYYY-MM
    if (!byMonth[month]) byMonth[month] = []
    byMonth[month].push(r.reading_value)
  })
  const trendData = Object.entries(byMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6)
    .map(([month, values]) => ({
      month: new Date(month + '-01').toLocaleString('sr-Latn-RS', { month: 'short' }),
      consumption: Math.round(values.reduce((s, v) => s + v, 0)),
    }))

  return { totalMeters, activeMeters, readingsThisMonth, coverage, trendData, readings }
}

function ScadaZoneReadoutsWidget() {
  return (
    <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-6">
      <h2 className="text-base font-semibold text-gray-900">SCADA — Očitavanja po zonama</h2>
      <p className="text-sm text-gray-400 mt-1 mb-4">Trenutne vrednosti senzora grupisane po SCADA zoni</p>
      <div className="border-2 border-dashed border-gray-200 rounded-xl h-40 flex flex-col items-center justify-center gap-2">
        <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="text-sm text-gray-400">Uskoro — grafički prikaz očitavanja po zoni</p>
      </div>
    </div>
  )
}

// GET /setup/edge-nodes is admin/company_admin-only server-side today;
// executive access is routed separately (handoffs/overwatch-to-scada-
// 2026-08-25-edge-nodes-executive-read.md) — build against it now per the
// routing handoff rather than blocking, an executive account may 403 here
// until that lands.
function EdgeServersWidget() {
  const { data: edgeNodes = [], isLoading } = useQuery({
    queryKey: ['scada-edge-nodes'],
    queryFn: () => scadaApi.listEdgeNodes().then((r) => r.data),
    refetchInterval: 30_000,
  })

  return (
    <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-1">Edge serveri</h2>
      <p className="text-sm text-gray-400 mb-4">Status povezanosti edge uređaja</p>
      {isLoading ? (
        <p className="text-sm text-gray-400">Učitavanje…</p>
      ) : edgeNodes.length === 0 ? (
        <p className="text-sm text-gray-400">Nema registrovanih edge servera.</p>
      ) : (
        <ul className="space-y-2.5">
          {edgeNodes.map((node) => {
            const status = edgeConnStatus(node)
            return (
              <li key={node.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-gray-700 truncate min-w-0">{node.name}</span>
                <span className="inline-flex items-center gap-1.5 shrink-0">
                  <span className={`w-2 h-2 rounded-full ${EDGE_STATUS_DOT[status]}`} />
                  <span className={`text-xs font-medium ${EDGE_STATUS_TEXT[status]}`}>{EDGE_STATUS_LABEL[status]}</span>
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// Reuses the existing dashed-border "Uskoro" convention
// (ScadaZoneReadoutsWidget below) rather than inventing a second one for
// this same "not built yet" state.
function MaintenanceFocus() {
  return (
    <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-6">
      <h2 className="text-base font-semibold text-gray-900">Održavanje</h2>
      <p className="text-sm text-gray-400 mt-1 mb-4">KPI, prioritetni nalozi, mapa reona i radni nalozi</p>
      <div className="border-2 border-dashed border-gray-200 rounded-xl h-40 flex flex-col items-center justify-center gap-2">
        <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
        <p className="text-sm text-gray-400">Uskoro — fokus na održavanje</p>
      </div>
    </div>
  )
}

// Today's dashboard content, essentially unchanged, wired as one focus
// among several (docs/plan-executive-dashboard-focuses.md).
function ConsumerFocus() {
  const { totalMeters, activeMeters, readingsThisMonth, coverage, trendData } = useReadingStats()
  const { user } = useAuthStore()
  const hasScada = user?.modules?.includes('scada') ?? false

  return (
    <>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          title="Vodomeri ukupno"
          value={String(totalMeters)}
          trend="stable"
          trendPercent={0}
          icon="readiness_score"
        />
        <KpiCard
          title="Aktivni vodomeri"
          value={String(activeMeters)}
          trend="stable"
          trendPercent={0}
          icon="readiness_score"
        />
        <KpiCard
          title="Očitavanja ovaj mesec"
          value={String(readingsThisMonth)}
          trend="up"
          trendPercent={0}
          icon="speed"
        />
        <KpiCard
          title="Pokrivenost"
          value={activeMeters > 0 ? `${coverage}%` : '—'}
          trend="stable"
          trendPercent={0}
          icon="percent"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">
              Trend potrošnje (m³)
            </h2>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v: number) => [`${v.toLocaleString()} m³`, 'Potrošnja']} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="consumption"
                    name="Potrošnja"
                    stroke="#37C8FC"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-gray-400">
                <p>Nema podataka o potrošnji</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Poslednja očitavanja</h2>
            <MeterTable />
          </div>

          {hasScada && <ScadaZoneReadoutsWidget />}
        </div>

        <div className="lg:col-span-1 space-y-6">
          {hasScada && <EdgeServersWidget />}
          <AlertsFeed />
        </div>
      </div>
    </>
  )
}

type FocusId = 'consumer' | 'maintenance' | 'infrastructure'

// Doc's own numbering (1-4 in the plan) is organizational only — the UI
// must show plain names, no literal numbers.
const FOCUS_DEFS: { id: FocusId; label: string; module: string }[] = [
  { id: 'consumer',       label: 'Potrošači',      module: 'meter_reading' },
  { id: 'maintenance',    label: 'Održavanje',     module: 'repair' },
  { id: 'infrastructure', label: 'Infrastruktura', module: 'scada' },
]

function FocusSwitcher({ focuses, active, onSelect }: {
  focuses: { id: FocusId; label: string }[]
  active: FocusId
  onSelect: (id: FocusId) => void
}) {
  return (
    <select
      value={active}
      onChange={(e) => onSelect(e.target.value as FocusId)}
      className="px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-lg bg-white shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
    >
      {focuses.map((f) => (
        <option key={f.id} value={f.id}>{f.label}</option>
      ))}
    </select>
  )
}

export default function ExecutiveDashboard() {
  const { user } = useAuthStore()
  // Same hasModule() SettingsPage.tsx's import-gating already established
  // (admin bypass, "no modules configured" reads as unrestricted) — one
  // shared module-gate check reused here rather than a fourth reimplementation.
  const availableFocuses = FOCUS_DEFS.filter((f) => hasModule(user, [f.module]))
  const [focus, setFocus] = useState<FocusId | null>(null)
  const activeFocus = focus ?? availableFocuses[0]?.id ?? null

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {availableFocuses.length > 0 && activeFocus && (
        <FocusSwitcher focuses={availableFocuses} active={activeFocus} onSelect={setFocus} />
      )}

      {activeFocus === 'consumer' && <ConsumerFocus />}
      {activeFocus === 'maintenance' && <MaintenanceFocus />}
      {activeFocus === 'infrastructure' && <InfrastructureFocus />}

      {!activeFocus && (
        <p className="text-sm text-gray-400">Nijedan modul nije aktivan za ovaj nalog.</p>
      )}
    </div>
  )
}

function MeterTable() {
  const { data: meters } = useQuery({
    queryKey: ['meters'],
    queryFn: () => readingsApi.getMeters({ limit: 500 }).then((r) => r.data),
    staleTime: 60_000,
  })

  const recent = [...(meters ?? [])]
    .filter((m) => m.last_reading_date !== null)
    .sort((a, b) => (b.last_reading_date ?? '').localeCompare(a.last_reading_date ?? ''))
    .slice(0, 5)

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-2 text-gray-500 font-medium">Korisnik</th>
            <th className="text-left py-2 text-gray-500 font-medium">Broj</th>
            <th className="text-right py-2 text-gray-500 font-medium">Datum</th>
            <th className="text-right py-2 text-gray-500 font-medium">Vrednost</th>
          </tr>
        </thead>
        <tbody>
          {recent.map((m) => (
            <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50">
              <td className="py-2 font-medium text-gray-900 truncate max-w-[120px]">{m.subscriber_name}</td>
              <td className="py-2 text-gray-500">{m.meter_serial}</td>
              <td className="py-2 text-right text-gray-500 text-xs tabular-nums">
                {m.last_reading_date ? m.last_reading_date.slice(0, 10) : '—'}
              </td>
              <td className="py-2 text-right text-gray-700 tabular-nums">
                {m.last_reading_value !== null ? `${m.last_reading_value.toFixed(2)} m³` : '—'}
              </td>
            </tr>
          ))}
          {recent.length === 0 && (
            <tr><td colSpan={4} className="py-4 text-center text-gray-400">Nema očitavanja</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
